<template>
  <div class="page-wrapper agent-sim-page waajo-agent-v2">
    <div class="content container-fluid">
      <div class="agent-sim-heading">
        <div>
          <router-link to="/agent-ai-v2" class="agent-sim-back">
            <i class="fa fa-arrow-left"></i> Kembali ke daftar agent
          </router-link>
          <h3>Simulasi Agent AI &amp; Chatbot</h3>
          <p>Uji respons agent menggunakan pesan masuk simulasi tanpa mengirim pesan ke pelanggan.</p>
        </div>
        <span class="agent-sim-mode-badge"><i class="fa fa-flask"></i> MODE SIMULASI</span>
      </div>

      <div class="agent-sim-notice">
        <i class="fa fa-info-circle"></i>
        <div>
          <strong>Ini hanya simulasi. Tidak ada pesan yang dikirim ke WhatsApp pelanggan.</strong>
          <span>Respons yang ditampilkan menggunakan konfigurasi agent yang tersimpan saat ini.</span>
        </div>
      </div>

      <div v-if="pageError" class="agent-sim-alert is-danger">
        <i class="fa fa-exclamation-circle"></i>
        <span>{{ pageError }}</span>
        <button type="button" class="btn btn-sm btn-outline-danger" @click="loadInitial">Coba lagi</button>
      </div>

      <div class="agent-sim-layout">
        <section class="agent-sim-chat-card">
          <div class="agent-sim-chat-head">
            <div>
              <h4><i class="fa fa-comments-o"></i> Percakapan Simulasi</h4>
              <p v-if="session">
                {{ session.config.license_name || "Tanpa nama" }} &middot;
                {{ session.config.license_phone_number || "-" }} &middot;
                {{ session.config.title || "Tanpa judul" }}
              </p>
              <p v-else>Pilih lisensi dan konfigurasi agent untuk memulai.</p>
            </div>
            <button
              type="button"
              class="btn btn-outline-secondary btn-sm"
              :disabled="!session || starting || sending"
              @click="resetSimulation"
            >
              <i class="fa fa-refresh"></i> Mulai Ulang
            </button>
          </div>

          <div ref="chatArea" class="agent-sim-chat-area">
            <div v-if="!messages.length" class="agent-sim-empty">
              <span class="agent-sim-empty-icon"><i class="fa fa-commenting-o"></i></span>
              <strong>Mulai percakapan simulasi</strong>
              <span>Ketik pesan pelanggan di bawah untuk melihat respons agent.</span>
            </div>

            <div
              v-for="message in messages"
              :key="message.id"
              class="agent-sim-message"
              :class="'is-' + message.role"
            >
              <div class="agent-sim-bubble">
                <div class="agent-sim-bubble-label">{{ message.label }}</div>
                <div class="agent-sim-bubble-text">{{ message.text }}</div>
                <div v-if="message.options && message.options.length" class="agent-sim-options">
                  <button
                    v-for="option in message.options"
                    :key="option.id"
                    type="button"
                    class="agent-sim-option"
                    :disabled="sending"
                    @click="sendTextReply(option)"
                  >
                    {{ option.order }}. {{ option.title }}
                  </button>
                </div>
                <div class="agent-sim-bubble-meta">
                  <span>{{ message.time }}</span>
                  <span v-if="message.role === 'agent'" class="agent-sim-checks"><i class="fa fa-check"></i><i class="fa fa-check"></i></span>
                </div>
              </div>
            </div>

            <div v-if="sending" class="agent-sim-message is-agent">
              <div class="agent-sim-bubble is-typing">
                <span class="agent-sim-typing-dot"></span>
                <span class="agent-sim-typing-dot"></span>
                <span class="agent-sim-typing-dot"></span>
                <small>Agent sedang memproses...</small>
              </div>
            </div>
          </div>

          <div class="agent-sim-input-row">
            <textarea
              v-model="inputMessage"
              class="agent-sim-input"
              rows="1"
              maxlength="4000"
              :disabled="!session || sending || starting"
              placeholder="Ketik pesan simulasi sebagai pelanggan..."
              @keydown.enter.exact.prevent="sendMessage"
            ></textarea>
            <button
              type="button"
              class="btn agent-sim-send"
              :disabled="!canSend"
              @click="sendMessage"
            >
              <i v-if="sending" class="fa fa-spinner fa-spin"></i>
              <i v-else class="fa fa-paper-plane"></i>
              Kirim
            </button>
          </div>
          <p class="agent-sim-input-hint">
            <i class="fa fa-info-circle"></i>
            Gunakan bahasa yang wajar seperti percakapan pelanggan sesungguhnya. Tekan Enter untuk mengirim.
          </p>
        </section>

        <aside class="agent-sim-side">
          <section class="agent-sim-panel">
            <div class="agent-sim-panel-head">
              <div>
                <span class="agent-sim-kicker">Konfigurasi Agent</span>
                <h4>Konfigurasi Agent Aktif</h4>
              </div>
              <span class="agent-sim-ready" :class="{ 'is-ready': session && sessionReady, 'is-warning': session && !sessionReady }">
                <i class="fa fa-circle"></i>
                {{ session ? (sessionReady ? "Siap disimulasikan" : "Perlu diperiksa") : "Belum dipilih" }}
              </span>
            </div>

            <div class="agent-sim-field">
              <label>Lisensi WhatsApp</label>
              <select v-model="selectedLicenseId" class="form-control" :disabled="starting || sending" @change="onLicenseChange">
                <option value="">Pilih lisensi</option>
                <option v-for="license in licenses" :key="license.id" :value="license.id">
                  {{ license.license_name || "Tanpa nama" }} - {{ license.license_phone_number || "-" }}
                </option>
              </select>
            </div>

            <div class="agent-sim-field">
              <label>Konfigurasi Agent</label>
              <select v-model="selectedConfigId" class="form-control" :disabled="!selectedLicenseId || configsLoading || starting || sending" @change="onConfigChange">
                <option value="">{{ configsLoading ? "Memuat konfigurasi..." : "Pilih konfigurasi agent" }}</option>
                <option v-for="config in configs" :key="config.id" :value="config.id">
                  {{ config.title || "Tanpa judul" }} - {{ modeLabel(config) }} - {{ config.is_publish == 1 ? "Aktif" : "Draft" }}
                </option>
              </select>
            </div>

            <div class="agent-sim-field">
              <label>Waktu Simulasi <small>(opsional)</small></label>
              <div class="agent-sim-time-row">
                <input v-model="simulatedAt" type="datetime-local" class="form-control" :disabled="!session || sending" />
                <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="!simulatedAt || sending" @click="simulatedAt = ''">
                  Waktu server
                </button>
              </div>
              <small class="agent-sim-help">
                Kosongkan untuk memakai waktu server. Isi waktu untuk menguji batas jam kerja.
              </small>
            </div>

            <div v-if="session" class="agent-sim-config-list">
              <div><span>Lisensi</span><strong>{{ session.config.license_name || "-" }}</strong></div>
              <div><span>Nomor WhatsApp</span><strong>{{ session.config.license_phone_number || "-" }}</strong></div>
              <div><span>Jenis Agent</span><strong>{{ session.config.agent_type === "ai" ? "Chat AI" : "Chatbot" }}</strong></div>
              <div v-if="session.config.agent_type === 'ai'"><span>Mode AI</span><strong>{{ session.config.ai_type_label }}</strong></div>
              <div><span>Mode Respons</span><strong>{{ session.config.mode_label }}</strong></div>
              <div><span>Status Konfigurasi</span><strong>{{ session.config.is_publish == 1 ? "Aktif" : "Draft (simulasi)" }}</strong></div>
              <div><span>Frekuensi</span><strong>{{ session.config.frequency_per_person }} menit</strong></div>
              <div><span>Status Lisensi</span><strong>{{ session.license_status }}</strong></div>
              <div><span>Zona Waktu</span><strong>{{ session.timezone }}</strong></div>
              <div><span>Waktu efektif</span><strong>{{ session.server_time }}</strong></div>
            </div>

            <div v-if="session && session.config.knowledge_base_used" class="agent-sim-kb">
              <i class="fa fa-book"></i>
              <div>
                <strong>Knowledge Base</strong>
                <small>{{ kbTotal }} dokumen tersedia untuk lisensi ini</small>
              </div>
            </div>

            <div v-if="session && session.config.agent_type === 'ai'" class="agent-sim-token">
              <i class="fa fa-key"></i>
              <div>
                <strong>Saldo token AI</strong>
                <small>{{ formatNumber(session.token_balance) }} token tersedia</small>
              </div>
            </div>

            <div v-if="session && session.warnings.length" class="agent-sim-warnings">
              <div v-for="(warning, index) in session.warnings" :key="'warning-' + index">
                <i class="fa fa-exclamation-triangle"></i>
                <span>{{ warning }}</span>
              </div>
            </div>

            <div v-if="modeEvaluation" class="agent-sim-mode-box">
              <span>Evaluasi mode saat ini</span>
              <strong>{{ modeEvaluation.mode_label }}</strong>
              <small>{{ modeEvaluation.reason }}</small>
            </div>
          </section>

          <section class="agent-sim-panel">
            <div class="agent-sim-panel-head">
              <div>
                <span class="agent-sim-kicker">Hasil</span>
                <h4>Hasil Respons Terakhir</h4>
              </div>
            </div>
            <div v-if="!lastResult" class="agent-sim-panel-empty">
              Kirim pesan simulasi untuk melihat hasil respons.
            </div>
            <div v-else class="agent-sim-result-list">
              <div><span>Status</span><strong :class="'is-' + lastResult.status">{{ lastResult.status_label }}</strong></div>
              <div><span>Sumber jawaban</span><strong>{{ sourceLabel(lastResult) }}</strong></div>
              <div><span>Waktu respons</span><strong>{{ formatDuration(lastResult.response_time_ms) }}</strong></div>
              <div><span>Waktu simulasi</span><strong>{{ lastResult.simulated_at || "-" }}</strong></div>
              <div v-if="lastResult.text_reply_selected">
                <span>Text Reply</span><strong>{{ lastResult.text_reply_selected.title }}</strong>
              </div>
              <div v-if="hasTokenUsage">
                <span>Token input</span><strong>{{ formatNumber(lastResult.usage.prompt_tokens) }}</strong>
              </div>
              <div v-if="hasTokenUsage">
                <span>Token output</span><strong>{{ formatNumber(lastResult.usage.completion_tokens) }}</strong>
              </div>
              <div v-if="hasTokenUsage">
                <span>Total token</span><strong>{{ formatNumber(lastResult.usage.total_tokens) }}</strong>
              </div>
              <div v-if="hasTokenUsage">
                <span>Token tercatat</span>
                <strong :class="{ 'is-warning-text': !lastResult.usage.billing_recorded }">
                  {{ lastResult.usage.billing_recorded ? formatNumber(lastResult.usage.billed_tokens) : "Belum terdeteksi" }}
                </strong>
              </div>
              <div v-if="hasTokenUsage" class="agent-sim-token-note">
                Pengujian Chat AI menggunakan token akun sesuai informasi billing yang berlaku.
              </div>
              <div v-if="lastResult.retrieval && lastResult.retrieval.used">
                <span>Knowledge Base</span>
                <strong :class="lastResult.retrieval.success ? 'is-success-text' : 'is-warning-text'">
                  {{ retrievalLabel(lastResult.retrieval) }}
                </strong>
              </div>
              <div v-if="lastResult.error" class="agent-sim-result-error">
                <span>Catatan</span><strong>{{ lastResult.error }}</strong>
              </div>
            </div>
          </section>

          <section class="agent-sim-panel">
            <div class="agent-sim-panel-head">
              <div>
                <!-- <span class="agent-sim-kicker">Jejak</span> -->
                <h4>Histori Pemrosesan</h4>
              </div>
            </div>
            <div v-if="!traceSteps.length" class="agent-sim-panel-empty">
              Histori simulasi akan tampil setelah pesan diproses.
            </div>
            <div v-else class="agent-sim-trace">
              <div v-for="step in traceSteps" :key="step.key" class="agent-sim-trace-item" :class="'is-' + step.status">
                <span class="agent-sim-trace-icon">
                  <i :class="traceIcon(step.status)"></i>
                </span>
                <div>
                  <strong>{{ step.label }}</strong>
                  <small>{{ step.detail }}</small>
                </div>
                <time>{{ step.time }}</time>
              </div>
            </div>
          </section>
        </aside>
      </div>

      <div v-if="showTokenWarning" class="agent-sim-modal-backdrop" @click.self="cancelTokenWarning">
        <div class="agent-sim-modal" role="dialog" aria-modal="true" aria-labelledby="agentSimTokenTitle">
          <div class="agent-sim-modal-icon"><i class="fa fa-key"></i></div>
          <h4 id="agentSimTokenTitle">Simulasi Chat AI menggunakan token</h4>
          <p>
            Setiap pesan yang Anda uji akan diproses oleh AI dan dapat mengurangi saldo token sesuai jumlah
            token input dan output. Simulasi tidak mengirim pesan WhatsApp kepada pelanggan, tetapi pemrosesan
            AI tetap menggunakan token.
          </p>
          <div class="agent-sim-modal-actions">
            <button type="button" class="btn btn-outline-secondary" @click="cancelTokenWarning">Batal</button>
            <button type="button" class="btn agent-btn-primary" @click="acceptTokenWarning">
              <i class="fa fa-check"></i> Saya Mengerti, Mulai Simulasi
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "AgentAiV2Simulation",
  data() {
    return {
      licenses: [],
      licensesLoading: false,
      selectedLicenseId: "",
      configs: [],
      configsLoading: false,
      selectedConfigId: "",
      session: null,
      starting: false,
      sending: false,
      pageError: "",
      inputMessage: "",
      messages: [],
      lastResult: null,
      simulatedAt: "",
      tokenAcknowledged: false,
      showTokenWarning: false,
      pendingSend: false,
      kbTotal: 0,
      messageSequence: 0
    };
  },
  computed: {
    canSend() {
      return !!(this.session && this.inputMessage.trim() && !this.sending && !this.starting);
    },
    modeEvaluation() {
      if (this.lastResult && this.lastResult.mode_evaluation) {
        return this.lastResult.mode_evaluation;
      }
      return this.session ? this.session.mode_evaluation : null;
    },
    sessionReady() {
      if (!this.session) return false;
      if (this.session.config.agent_type === "ai" && Number(this.session.token_balance) <= 0) {
        return false;
      }
      if (this.session.config.is_publish == 0) return true;
      const evaluation =
        (this.lastResult && this.lastResult.mode_evaluation) ||
        this.session.mode_evaluation;
      return evaluation ? evaluation.active : true;
    },
    hasTokenUsage() {
      const usage = this.lastResult && this.lastResult.usage;
      if (!usage) return false;
      return (
        Number(usage.prompt_tokens) > 0 ||
        Number(usage.completion_tokens) > 0 ||
        Number(usage.total_tokens) > 0 ||
        !!usage.billing_recorded
      );
    },
    traceSteps() {
      const steps = this.lastResult && this.lastResult.trace ? this.lastResult.trace : [];
      const hiddenKeys = { provider: true, chat_completions: true };
      return steps
        .filter((step) => step && !hiddenKeys[step.key])
        .map((step) => {
          const item = Object.assign({}, step);
          if (step.key === "received") {
            item.label = "Pesan diterima";
            item.detail = "Pesan simulasi diterima dan siap diproses.";
          } else if (step.key === "config") {
            item.label = "Pengaturan diperiksa";
          } else if (step.key === "frequency") {
            item.label = "Aturan frekuensi diperiksa";
          } else if (step.key === "knowledge_base") {
            item.label = "Pengetahuan dicari";
            item.detail = String(step.detail || "")
              .replace(/\bchunks?\b/gi, "bagian teks")
              .replace(/Retrieval gagal:.*/i, "Pencarian pengetahuan tidak berhasil. Coba lagi atau periksa pengaturan Knowledge Base.");
          } else if (step.key === "prompt") {
            item.label = "Instruksi jawaban disiapkan";
            item.detail = "Pengaturan instruksi dan konteks percakapan disiapkan.";
          } else if (step.key === "ai_done") {
            item.label = "Jawaban dibuat";
            item.detail = "Jawaban berhasil dibuat.";
          } else if (step.key === "ai_error") {
            item.label = "Jawaban gagal dibuat";
            if (/provider ai|deepseek|openai|gpt-|gemini|claude|anthropic|api key|credential|unauthorized|qdrant|embedding|ollama/i.test(String(step.detail || ""))) {
              item.detail = "Layanan AI gagal memproses permintaan. Coba lagi beberapa saat.";
            }
          } else if (step.key === "ai_empty") {
            item.label = "Jawaban belum tersedia";
          } else if (step.key === "billing") {
            item.label = step.status === "warning" ? "Penggunaan token" : "Penggunaan token dicatat";
          } else if (step.key === "intent") {
            item.label = "Tindakan lanjutan terdeteksi";
            item.detail = "Tindakan lanjutan tidak dijalankan dalam simulasi.";
          }
          return item;
        });
    }
  },
  created() {
    this.loadInitial();
  },
  methods: {
    async loadInitial() {
      this.pageError = "";
      this.licensesLoading = true;
      try {
        const response = await this.$store.dispatch("license/getLicenseList", {
          license_name: "",
          license_phone_number: "",
          license_status: "",
          page: 0,
          perPage: 200
        });
        this.licenses = (response.data && response.data.data) || [];
        const query = this.$route.query || {};
        const licenseId = query.license_id || "";
        if (licenseId) this.selectedLicenseId = licenseId;
        if (!this.selectedLicenseId && this.licenses.length) {
          this.selectedLicenseId = this.licenses[0].id;
        }
        if (this.selectedLicenseId) {
          await this.loadConfigs();
          if (query.config_id && this.configs.some(item => item.id === query.config_id)) {
            this.selectedConfigId = query.config_id;
            await this.startSimulation();
          } else if (this.configs.length) {
            this.selectedConfigId = this.configs[0].id;
            await this.startSimulation();
          }
        }
      } catch (error) {
        this.pageError = this.errorMessage(error, "Data simulasi belum berhasil dimuat.");
      } finally {
        this.licensesLoading = false;
      }
    },
    async loadConfigs() {
      if (!this.selectedLicenseId) {
        this.configs = [];
        return;
      }
      this.configsLoading = true;
      this.pageError = "";
      try {
        const response = await this.$store.dispatch("automation/listSmartReply", {
          columnFilters: {
            license_id: this.selectedLicenseId,
            title: "",
            message: "",
            trigger_on: "",
            is_working_or_away: "",
            is_working: ""
          },
          page: 0,
          perPage: 200
        });
        this.configs = ((response.data && response.data.data) || []).filter(
          row => row.trigger_on === "all"
        );
      } catch (error) {
        this.configs = [];
        this.pageError = this.errorMessage(error, "Daftar konfigurasi agent belum berhasil dimuat.");
      } finally {
        this.configsLoading = false;
      }
    },
    async onLicenseChange() {
      this.session = null;
      this.selectedConfigId = "";
      this.messages = [];
      this.lastResult = null;
      this.pageError = "";
      this.kbTotal = 0;
      await this.loadConfigs();
      if (this.configs.length) {
        this.selectedConfigId = this.configs[0].id;
        await this.startSimulation();
      }
    },
    async onConfigChange() {
      this.session = null;
      this.messages = [];
      this.lastResult = null;
      this.pageError = "";
      this.kbTotal = 0;
      if (!this.selectedConfigId) return;
      await this.startSimulation();
    },
    async startSimulation() {
      if (!this.selectedLicenseId || !this.selectedConfigId || this.starting) return;
      this.starting = true;
      this.pageError = "";
      try {
        const response = await this.$store.dispatch("automation/startAgentV2Simulation", {
          license_id: this.selectedLicenseId,
          config_id: this.selectedConfigId,
          simulated_at: this.simulatedAt || ""
        });
        this.session = response.data;
        this.messages = [];
        this.lastResult = null;
        this.tokenAcknowledged = false;
        if (this.session.config.knowledge_base_used) {
          await this.loadKbTotal();
        }
        this.scrollToBottom();
      } catch (error) {
        this.pageError = this.errorMessage(error, "Sesi simulasi belum berhasil dimulai.");
      } finally {
        this.starting = false;
      }
    },
    async loadKbTotal() {
      if (!this.selectedLicenseId) return;
      try {
        const response = await this.$store.dispatch("aiKey/historyListKB", {
          page: 0,
          perPage: 50,
          columnFilters: { license_id: this.selectedLicenseId }
        });
        const rows = (response.data && response.data.data) || [];
        this.kbTotal = Number(response.data && response.data.totalRecords) || rows.length;
      } catch (error) {
        this.kbTotal = 0;
      }
    },
    async sendMessage() {
      if (!this.canSend) return;
      if (this.session.config.agent_type === "ai" && !this.tokenAcknowledged) {
        this.pendingSend = true;
        this.showTokenWarning = true;
        return;
      }
      await this.performSend(this.inputMessage.trim(), "");
      this.inputMessage = "";
    },
    async sendTextReply(option) {
      if (!option || this.sending) return;
      await this.performSend(String(option.order), option.id);
    },
    async performSend(text, textReplyId) {
      if (!this.session || this.sending || !text) return;
      this.sending = true;
      this.pageError = "";
      this.messages.push({
        id: "customer-" + (++this.messageSequence),
        role: "customer",
        label: "Pelanggan (Simulasi)",
        text: text,
        time: this.clockNow()
      });
      this.scrollToBottom();
      try {
        const response = await this.$store.dispatch("automation/sendAgentV2Simulation", {
          session_id: this.session.session_id,
          message: text,
          text_reply_id: textReplyId || "",
          simulated_at: this.simulatedAt || ""
        });
        const result = response.data || {};
        if (result.error) {
          result.error = this.errorMessage({ message: result.error }, result.error);
        }
        this.lastResult = result;
        if (result.reply) {
          this.messages.push({
            id: "agent-" + (++this.messageSequence),
            role: "agent",
            label: this.session.config.agent_type === "ai" ? "Agent AI (Chat AI)" : "Agent (Chatbot)",
            text: result.reply,
            time: this.clockNow(),
            options: result.options || []
          });
        } else if (result.error) {
          this.messages.push({
            id: "system-" + (++this.messageSequence),
            role: "system",
            label: "Hasil Simulasi",
            text: result.error,
            time: this.clockNow()
          });
        }
        if (result.session_id) {
          this.session.session_id = result.session_id;
        }
        if (result.usage && typeof result.usage.balance_after === "number") {
          this.session.token_balance = result.usage.balance_after;
        }
      } catch (error) {
        const message = this.errorMessage(error, "Pesan simulasi belum berhasil diproses.");
        this.lastResult = {
          status: "failed",
          status_label: "Gagal",
          error: message,
          source: "",
          response_time_ms: 0,
          trace: [],
          options: []
        };
        this.messages.push({
          id: "system-" + (++this.messageSequence),
          role: "system",
          label: "Gagal Memproses",
          text: message,
          time: this.clockNow()
        });
      } finally {
        this.sending = false;
        this.scrollToBottom();
      }
    },
    async resetSimulation() {
      if (!this.session || this.starting || this.sending) return;
      this.starting = true;
      this.pageError = "";
      try {
        const response = await this.$store.dispatch("automation/resetAgentV2Simulation", {
          session_id: this.session.session_id
        });
        this.session = response.data;
        this.messages = [];
        this.lastResult = null;
        this.tokenAcknowledged = false;
        this.showTokenWarning = false;
        this.pendingSend = false;
        this.inputMessage = "";
        if (this.session.config.knowledge_base_used) {
          await this.loadKbTotal();
        }
        this.scrollToBottom();
      } catch (error) {
        this.pageError = this.errorMessage(error, "Sesi simulasi belum berhasil direset.");
      } finally {
        this.starting = false;
      }
    },
    acceptTokenWarning() {
      this.tokenAcknowledged = true;
      this.showTokenWarning = false;
      if (this.pendingSend) {
        this.pendingSend = false;
        const text = this.inputMessage.trim();
        this.inputMessage = "";
        if (text) this.performSend(text, "");
      }
    },
    cancelTokenWarning() {
      this.showTokenWarning = false;
      this.pendingSend = false;
    },
    modeLabel(config) {
      if (!config) return "-";
      if (Number(config.is_working_or_away) === 0) return "Balas Otomatis 24 Jam";
      return Number(config.is_working) === 1 ? "Saat Jam Kerja" : "Di Luar Jam Kerja";
    },
    sourceLabel(result) {
      if (!result) return "-";
      if (result.retrieval && result.retrieval.used) {
        return result.retrieval.success ? "Knowledge Base" : "Knowledge Base (gagal)";
      }
      if (result.source === "chat_ai") return "Chat AI";
      if (result.source === "chatbot") return "Chatbot / Text Reply";
      return "-";
    },
    retrievalLabel(retrieval) {
      if (!retrieval) return "-";
      if (!retrieval.success) return "Tidak berhasil";
      const documents = this.formatNumber(retrieval.document_count);
      const sections = this.formatNumber(retrieval.chunk_count);
      return documents + " dokumen, " + sections + " bagian teks relevan";
    },
    formatDuration(ms) {
      const value = Number(ms) || 0;
      if (value < 1000) return value + " ms";
      return (value / 1000).toFixed(1).replace(".", ",") + " detik";
    },
    formatNumber(value) {
      const number = Number(value) || 0;
      return number.toLocaleString("id-ID");
    },
    clockNow() {
      return new Date().toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" });
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const area = this.$refs.chatArea;
        if (area) area.scrollTop = area.scrollHeight;
      });
    },
    traceIcon(status) {
      if (status === "success") return "fa fa-check";
      if (status === "warning") return "fa fa-exclamation";
      if (status === "danger") return "fa fa-times";
      return "fa fa-circle";
    },
    errorMessage(error, fallback) {
      const message = (error && (error.message || error.msg)) || "";
      if (!message) return fallback;
      if (/sesi simulasi tidak ditemukan|kedaluwarsa/i.test(message)) {
        return "Sesi simulasi sudah berakhir. Klik Mulai Ulang atau pilih konfigurasi kembali.";
      }
      if (/deepseek|provider ai|api key|credential|unauthorized|\bmodel\s+[a-z0-9._-]+/i.test(message)) {
        return "Layanan AI belum dapat memproses permintaan. Coba lagi beberapa saat atau hubungi admin.";
      }
      if (/token/i.test(message)) {
        return message.indexOf("token") !== -1 ? message : "Saldo token AI tidak cukup atau belum tersedia.";
      }
      return message;
    }
  }
};
</script>

<style scoped>
.agent-sim-page {
  color: #344054;
  padding-bottom: 40px;
}
.agent-sim-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}
.agent-sim-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  color: #7b8ba3;
  font-size: 12px;
  font-weight: 700;
}
.agent-sim-heading h3 {
  margin: 0 0 4px;
  color: #172033;
  font-size: 28px;
  font-weight: 800;
}
.agent-sim-heading p {
  margin: 0;
  color: #7b8ba3;
  font-size: 13px;
}
.agent-sim-mode-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #f7c98b;
  border-radius: 8px;
  background: #fff8f0;
  color: #b45309;
  padding: 7px 10px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: .04em;
}
.agent-sim-notice {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 16px;
  border: 1px solid #cfe0ff;
  border-radius: 12px;
  background: #f3f8ff;
  color: #175cd3;
  padding: 12px 14px;
}
.agent-sim-notice strong,
.agent-sim-notice span {
  display: block;
}
.agent-sim-notice strong {
  font-size: 12px;
  font-weight: 800;
}
.agent-sim-notice span {
  margin-top: 2px;
  color: #5b7fb9;
  font-size: 12px;
}
.agent-sim-alert {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 12px;
}
.agent-sim-alert.is-danger {
  border: 1px solid #f6c9c3;
  background: #fff8f7;
  color: #b42318;
}
.agent-sim-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(340px, .9fr);
  gap: 16px;
  align-items: start;
}
.agent-sim-chat-card,
.agent-sim-panel {
  border: 1px solid #e7eaf0;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(16, 24, 40, .04);
}
.agent-sim-chat-card {
  overflow: hidden;
}
.agent-sim-chat-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  border-bottom: 1px solid #eef1f5;
  padding: 15px 17px;
}
.agent-sim-chat-head h4 {
  margin: 0 0 3px;
  color: #172033;
  font-size: 16px;
  font-weight: 800;
}
.agent-sim-chat-head h4 i {
  margin-right: 5px;
  color: #98a2b3;
}
.agent-sim-chat-head p {
  margin: 0;
  color: #7b8ba3;
  font-size: 12px;
}
.agent-sim-chat-area {
  height: 520px;
  overflow-y: auto;
  padding: 18px;
  background-color: #f7f8f4;
  background-image: radial-gradient(#e8ebe1 .7px, transparent .7px);
  background-size: 18px 18px;
}
.agent-sim-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #7b8ba3;
  text-align: center;
}
.agent-sim-empty-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  margin-bottom: 10px;
  border-radius: 50%;
  background: #fff3e3;
  color: #e77f25;
  font-size: 24px;
}
.agent-sim-empty strong {
  color: #475467;
  font-size: 13px;
}
.agent-sim-empty span {
  margin-top: 3px;
  font-size: 12px;
}
.agent-sim-message {
  display: flex;
  margin-bottom: 12px;
}
.agent-sim-message.is-agent {
  justify-content: flex-end;
}
.agent-sim-message.is-system {
  justify-content: center;
}
.agent-sim-bubble {
  max-width: 78%;
  border: 1px solid #ececec;
  border-radius: 4px 12px 12px 12px;
  background: #fff;
  padding: 10px 12px 7px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, .04);
}
.agent-sim-message.is-agent .agent-sim-bubble {
  border-color: #d5f0dd;
  border-radius: 12px 4px 12px 12px;
  background: #e7f8ea;
}
.agent-sim-message.is-system .agent-sim-bubble {
  max-width: 90%;
  border-color: #f1d4cf;
  border-radius: 9px;
  background: #fff7f5;
}
.agent-sim-bubble-label {
  margin-bottom: 3px;
  color: #667085;
  font-size: 12px;
  font-weight: 800;
}
.agent-sim-message.is-agent .agent-sim-bubble-label {
  color: #17803d;
}
.agent-sim-bubble-text {
  color: #344054;
  font-size: 12px;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
}
.agent-sim-bubble-meta {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 5px;
  margin-top: 3px;
  color: #98a2b3;
  font-size: 12px;
}
.agent-sim-checks {
  color: #2e90fa;
  font-size: 12px;
  letter-spacing: -2px;
}
.agent-sim-options {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 7px;
}
.agent-sim-option {
  border: 1px solid #b7e4c7;
  border-radius: 7px;
  background: #fff;
  color: #17803d;
  padding: 4px 7px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}
.agent-sim-option:hover {
  background: #f1fbf4;
}
.agent-sim-bubble.is-typing {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #7b8ba3;
}
.agent-sim-bubble.is-typing small {
  margin-left: 4px;
  font-size: 12px;
}
.agent-sim-typing-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #98a2b3;
  animation: agent-sim-blink 1.2s infinite;
}
.agent-sim-typing-dot:nth-child(2) { animation-delay: .2s; }
.agent-sim-typing-dot:nth-child(3) { animation-delay: .4s; }
@keyframes agent-sim-blink {
  0%, 80%, 100% { opacity: .25; }
  40% { opacity: 1; }
}
.agent-sim-input-row {
  display: flex;
  gap: 8px;
  border-top: 1px solid #eef1f5;
  padding: 12px 15px 5px;
}
.agent-sim-input {
  flex: 1 1 auto;
  min-height: 42px;
  max-height: 110px;
  border: 1px solid #e1e6ee;
  border-radius: 9px;
  padding: 10px 12px;
  color: #344054;
  font-size: 12px;
  resize: vertical;
}
.agent-sim-input:focus {
  outline: none;
  border-color: #f5b36c;
  box-shadow: 0 0 0 3px rgba(254, 155, 68, .12);
}
.agent-sim-send {
  align-self: flex-end;
  border: 0;
  border-radius: 9px;
  background: #fe9b44;
  color: #fff;
  padding: 10px 18px;
  font-size: 12px;
  font-weight: 800;
}
.agent-sim-send:hover:not(:disabled) {
  background: #e77f25;
}
.agent-sim-send:disabled {
  opacity: .55;
}
.agent-sim-input-hint {
  margin: 0;
  padding: 0 15px 12px;
  color: #98a2b3;
  font-size: 12px;
}
.agent-sim-side {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.agent-sim-panel {
  padding: 14px;
}
.agent-sim-panel-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 9px;
  margin-bottom: 11px;
}
.agent-sim-kicker {
  display: block;
  color: #98a2b3;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
}
.agent-sim-panel-head h4 {
  margin: 2px 0 0;
  color: #172033;
  font-size: 13px;
  font-weight: 800;
}
.agent-sim-ready {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: 999px;
  background: #f2f4f7;
  color: #667085;
  padding: 4px 7px;
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
}
.agent-sim-ready i {
  font-size: 5px;
}
.agent-sim-ready.is-ready {
  background: #e8f7ee;
  color: #0b8f4d;
}
.agent-sim-ready.is-warning {
  background: #fff4e8;
  color: #b45309;
}
.agent-sim-field {
  margin-bottom: 9px;
}
.agent-sim-field label {
  display: block;
  margin-bottom: 4px;
  color: #475467;
  font-size: 12px;
  font-weight: 800;
}
.agent-sim-field label small {
  color: #98a2b3;
  font-weight: 600;
}
.agent-sim-field .form-control {
  height: 34px;
  border-color: #e1e6ee;
  color: #344054;
  font-size: 12px;
}
.agent-sim-time-row {
  display: flex;
  gap: 6px;
}
.agent-sim-time-row .form-control {
  flex: 1 1 auto;
}
.agent-sim-help {
  display: block;
  margin-top: 3px;
  color: #98a2b3;
  font-size: 12px;
  line-height: 1.4;
}
.agent-sim-config-list {
  display: grid;
  gap: 6px;
  margin-top: 4px;
  padding-top: 9px;
  border-top: 1px solid #eef1f5;
}
.agent-sim-config-list > div,
.agent-sim-result-list > div {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  font-size: 12px;
}
.agent-sim-config-list span,
.agent-sim-result-list span {
  color: #7b8ba3;
}
.agent-sim-config-list strong,
.agent-sim-result-list strong {
  color: #344054;
  font-weight: 800;
  text-align: right;
}
.agent-sim-kb,
.agent-sim-token {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  border: 1px solid #e7eaf0;
  border-radius: 8px;
  background: #fbfcfe;
  padding: 8px 9px;
}
.agent-sim-kb i,
.agent-sim-token i {
  color: #e77f25;
}
.agent-sim-kb strong,
.agent-sim-token strong {
  display: block;
  color: #344054;
  font-size: 12px;
  font-weight: 800;
}
.agent-sim-kb small,
.agent-sim-token small {
  display: block;
  color: #7b8ba3;
  font-size: 12px;
}
.agent-sim-warnings {
  display: grid;
  gap: 5px;
  margin-top: 10px;
}
.agent-sim-warnings > div {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  border-radius: 7px;
  background: #fffbf3;
  color: #7a4b08;
  padding: 6px 8px;
  font-size: 12px;
  line-height: 1.4;
}
.agent-sim-mode-box {
  margin-top: 10px;
  border-radius: 8px;
  background: #f5f9ff;
  padding: 8px 9px;
}
.agent-sim-mode-box span,
.agent-sim-mode-box strong,
.agent-sim-mode-box small {
  display: block;
}
.agent-sim-mode-box span {
  color: #5b7fb9;
  font-size: 12px;
  font-weight: 700;
}
.agent-sim-mode-box strong {
  margin-top: 2px;
  color: #175cd3;
  font-size: 12px;
  font-weight: 800;
}
.agent-sim-mode-box small {
  margin-top: 2px;
  color: #5b7fb9;
  font-size: 12px;
  line-height: 1.4;
}
.agent-sim-panel-empty {
  color: #98a2b3;
  font-size: 12px;
  line-height: 1.5;
}
.agent-sim-result-list {
  display: grid;
  gap: 6px;
}
.agent-sim-result-list .is-success,
.agent-sim-result-list .is-success-text {
  color: #0b8f4d;
}
.agent-sim-result-list .is-failed,
.agent-sim-result-list .is-danger,
.agent-sim-result-list .is-warning-text {
  color: #b42318;
}
.agent-sim-result-list .is-no_response {
  color: #b45309;
}
.agent-sim-result-error {
  margin-top: 4px;
  border-top: 1px solid #eef1f5;
  padding-top: 6px;
}
.agent-sim-result-error strong {
  color: #b42318;
  font-size: 12px;
  text-align: right;
}
.agent-sim-result-list > .agent-sim-token-note {
  display: block;
  border-top: 1px solid #eef1f5;
  padding-top: 6px;
  color: #98a2b3;
  font-size: 12px;
  line-height: 1.45;
  text-align: left;
}
.agent-sim-trace {
  display: grid;
  gap: 0;
}
.agent-sim-trace-item {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding-bottom: 12px;
}
.agent-sim-trace-item:not(:last-child)::before {
  content: "";
  position: absolute;
  top: 20px;
  left: 9px;
  width: 1px;
  height: calc(100% - 8px);
  background: #e7eaf0;
}
.agent-sim-trace-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 18px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #f2f4f7;
  color: #667085;
  font-size: 12px;
}
.agent-sim-trace-item.is-success .agent-sim-trace-icon {
  background: #e8f7ee;
  color: #0b8f4d;
}
.agent-sim-trace-item.is-warning .agent-sim-trace-icon {
  background: #fff4e8;
  color: #b45309;
}
.agent-sim-trace-item.is-danger .agent-sim-trace-icon {
  background: #fff1ed;
  color: #b42318;
}
.agent-sim-trace-item > div {
  flex: 1 1 auto;
  min-width: 0;
}
.agent-sim-trace-item strong {
  display: block;
  color: #344054;
  font-size: 12px;
  font-weight: 800;
}
.agent-sim-trace-item small {
  display: block;
  margin-top: 1px;
  color: #7b8ba3;
  font-size: 12px;
  line-height: 1.35;
}
.agent-sim-trace-item time {
  flex: 0 0 auto;
  color: #98a2b3;
  font-size: 12px;
}
.agent-sim-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2100;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(16, 24, 40, .55);
  padding: 16px;
}
.agent-sim-modal {
  width: 100%;
  max-width: 470px;
  border-radius: 14px;
  background: #fff;
  padding: 22px;
  box-shadow: 0 24px 64px rgba(16, 24, 40, .28);
}
.agent-sim-modal-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  margin-bottom: 10px;
  border-radius: 12px;
  background: #fff3e3;
  color: #e77f25;
  font-size: 19px;
}
.agent-sim-modal h4 {
  margin: 0 0 6px;
  color: #172033;
  font-size: 18px;
  font-weight: 800;
}
.agent-sim-modal p {
  margin: 0;
  color: #667085;
  font-size: 12px;
  line-height: 1.6;
}
.agent-sim-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
}
.agent-btn-primary {
  border: 0;
  background: #fe9b44;
  color: #fff;
  font-weight: 800;
}
.agent-btn-primary:hover {
  background: #e77f25;
  color: #fff;
}
@media (max-width: 1100px) {
  .agent-sim-layout {
    grid-template-columns: minmax(0, 1fr);
  }
  .agent-sim-chat-area {
    height: 430px;
  }
}
@media (max-width: 767px) {
  .agent-sim-heading {
    flex-direction: column;
  }
  .agent-sim-chat-head {
    flex-direction: column;
  }
  .agent-sim-chat-area {
    height: 360px;
    padding: 12px;
  }
  .agent-sim-bubble {
    max-width: 92%;
  }
  .agent-sim-input-row {
    flex-direction: column;
  }
  .agent-sim-send {
    width: 100%;
  }
  .agent-sim-modal-actions {
    flex-direction: column-reverse;
  }
  .agent-sim-modal-actions .btn {
    width: 100%;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/ai/agentV2/simulation.vue